// CoinContentList.jsx
import React, { useContext, useEffect, useMemo, useState } from "react";
import { ConstantsContext } from "../index";
import CoinLogo from "./CoinLogo";

export default function CoinContentList({
  userDetails,
  _apiConnect,
  jsonField,           // "coin_blog_json" | "coin_article_json"
  title = "Coin Content",
  embed = false,       // when true, no outer max-w wrapper
}) {
  const [staticText] = useContext(ConstantsContext);
  const [rows, setRows] = useState([]);
  const [sortMode, setSortMode] = useState("latest"); // "alpha" | "latest" | "sentiment"
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [showAll, setShowAll] = useState(false);       // first 3, then reveal more

  // --- API glue (same as your other sections) ---
  const _api_get_pnl_public = (_month = "") => {
    const data = [
      { key: "loggined_token", value: userDetails?.loggined_token },
      { key: "action", value: "get_pnl_public" },
      { key: "filterByMonthValue", value: _month },
    ];
    _apiConnect(staticText.path_darts, data, (res) => {
      if (res?.status_code === 2) setRows(res.cmd_news_flash || []);
    });
  };

  useEffect(() => {
    _api_get_pnl_public("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const parseJsonSafe = (s) => {
    try { return s ? JSON.parse(s) : null; } catch { return null; }
  };

  // Insert commas into 4+ digit numbers (keeps decimals, leaves already-comma'd values)
  const formatNumbersInText = (str) => {
    if (!str) return str;
    let out = String(str);

    // 1) Percentages: "12.345 %" or "12.345%"
    out = out.replace(/(-?\d+(?:\.\d+)?)(\s*%)/g, (_, num) => `${formatPercent(num)}%`);

    // 2) Dollar-prefixed prices/levels: "$1234.56789"
    out = out.replace(/\$(-?\d+(?:\.\d+)?)/g, (_, num) => `$${formatPriceLevel(num)}`);

    // 3) Bare numbers that look like prices/levels.
    //    Heuristics: either 4+ digits (likely level) OR any decimal number.
    //    Avoid touching parts of words/ids (skip if directly adjacent to word char, #, or hyphen).
    out = out.replace(/(^|[^A-Za-z0-9#-])(-?\d+(?:\.\d+)?)(?![A-Za-z0-9.%-])/g, (m, lead, num) => {
      // Skip if this number was already formatted as part of $… or …% (handled above)
      // Also skip weird decimals like "0.2,071"
      if (/,/.test(num.split('.')[1] || '')) return m;

      const abs = Math.abs(Number(num));
      const shouldFormat =
        !isNaN(abs) && (num.includes('.') || num.replace('-', '').length >= 4);

      return shouldFormat ? `${lead}${formatPriceLevel(num)}` : m;
    });

    return out;
  };

  function formatPriceLevel(value) {
    const n = Number(value);
    const abs = Math.abs(n);

    if (!isFinite(n)) return String(value);

    if (abs >= 1) {
      // Exactly 2 decimals
      return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    } else if (abs >= 1e-4) {
      // Up to 4 decimals, trim trailing zeros
      return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 4 });
    } else {
      // Up to 8 decimals, trim trailing zeros
      return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 8 });
    }
  }

  // Percentages: TP1, TP2, TP3, RISK_CAP (number only; append % where you print it)
  function formatPercent(value) {
    const n = Number(value);
    if (!isFinite(n)) return String(value);
    return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  }

  // normalize & filter items that have the requested jsonField content
  const items = useMemo(() => {
    const list = (rows || [])
      .map((n) => {
        const payload = parseJsonSafe(n?.[jsonField]);
        if (!payload) return null;
        const dateTs = Number(new Date(n?.date)) || 0;

        return {
          id: n.id,
          coin_pair_name: n.coin_pair_name,
          date_ago: n.date_ago,
          dateTs, // for reliable "Latest" sorting
          headline_sentiment: (payload.sentiment || "neutral").toLowerCase(),
          title: formatNumbersInText(payload.title || ""),                 // ← update
          meta_description: formatNumbersInText(payload.meta_description || ""), // ← update
          content: formatNumbersInText(payload.content || ""),             // ← update
          detailsHref: `https://cryptomarketdarts.com/public_prediction.php?id=${n.id}`,
        };
      })
      .filter(Boolean);

    // sort
    const sorted = [...list];
    if (sortMode === "alpha") {
      sorted.sort((a, b) => {
        const A = (a.coin_pair_name || "").localeCompare(b.coin_pair_name || "");
        if (A !== 0) return A;
        return 0;
      });
    } else if (sortMode === "latest") {
      // newest first
      sorted.sort((a, b) => (b.dateTs || 0) - (a.dateTs || 0));
    } else if (sortMode === "sentiment") {
      const rank = { bullish: 0, neutral: 1, bearish: 2 };
      sorted.sort((a, b) => (rank[a.headline_sentiment] ?? 1) - (rank[b.headline_sentiment] ?? 1));
    }
    return sorted;
  }, [rows, jsonField, sortMode]);

  const visible = useMemo(() => (showAll ? items : items.slice(0, 3)), [items, showAll]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const Root = ({ children }) =>
    embed ? (
      <div className="w-full">{children}</div>
    ) : (
      <section className="relative">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">{children}</div>
      </section>
    );

  const SentimentDot = ({ v }) => (
    <span
      className="inline-block w-2.5 h-2.5 rounded-full border border-black/20"
      style={{
        background:
          v === "bullish" ? "#00b050" : v === "bearish" ? "#d9534f" : "#808080",
        borderColor:
          v === "bullish" ? "#00b050" : v === "bearish" ? "#d9534f" : "#808080",
      }}
    />
  );

  return (
    <Root>
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        <div className="flex items-center gap-1 text-xs">
          <span className="hidden sm:inline text-slate-400 mr-1">Sort by:</span>
          {/* Order: Alphabetical, Latest, Sentiment */}
          {["alpha", "latest", "sentiment"].map((k) => (
            <button
              key={k}
              onClick={() => setSortMode(k)}
              className={`rounded-full px-3 py-1 border border-white/10 ${
                sortMode === k ? "bg-white/10" : "bg-transparent hover:bg-white/10"
              }`}
            >
              {k === "alpha" ? "Alphabetical" : k[0].toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <ul className="space-y-3">
        {visible.map((n) => {
          const isOpen = expandedIds.has(n.id);
          return (
            <li
              key={n.id}
              className="rounded-2xl border border-white/10 bg-gradient-to-br from-slate-900/60 to-slate-900/20 p-4 hover:border-white/20 transition"
            >
              <div className="flex items-start gap-3">
                <div className="mt-0.5">
                  <CoinLogo coinPair={n.coin_pair_name} />
                </div>

                <div className="flex-1 min-w-0">
                  {/* pair • time • link */}
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-wider text-slate-400 flex items-center gap-2">
                      <span className="font-semibold text-slate-200">{n.coin_pair_name}</span>
                      <span className="hidden sm:inline">•</span>
                      <span>{n.date_ago}</span>
                    </div>
                    <a
                      href={n.detailsHref}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-emerald-400 hover:text-emerald-300"
                    >
                      Details...
                    </a>
                  </div>

                  {/* collapsed: title + meta + sentiment; expanded: content + sentiment */}
                  {!isOpen ? (
                    <>
                      <div className="mt-1 flex items-center gap-2">
                        <SentimentDot v={n.headline_sentiment} />
                        <p className="text-sm text-slate-200">
                          <strong>{n.title}</strong>{" "}
                          <span className="text-slate-400">
                            (
                            {n.headline_sentiment.charAt(0).toUpperCase() +
                              n.headline_sentiment.slice(1)}
                            )
                          </span>
                        </p>
                      </div>
                      {n.meta_description && (
                        <p className="mt-1 text-sm text-slate-300">
                          {n.meta_description}
                        </p>
                      )}

                      <button
                        type="button"
                        onClick={() => toggleExpanded(n.id)}
                        className="mt-2 text-xs text-emerald-400 hover:text-emerald-300"
                      >
                        Read More
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="mt-1 flex items-center gap-2">
                        <SentimentDot v={n.headline_sentiment} />
                        <span className="text-xs text-slate-400">
                          {n.headline_sentiment.charAt(0).toUpperCase() +
                            n.headline_sentiment.slice(1)}
                        </span>
                      </div>
                      <div className="mt-1 text-sm text-slate-200 whitespace-pre-wrap">
                        {n.content}
                      </div>
                      <button
                        type="button"
                        onClick={() => toggleExpanded(n.id)}
                        className="mt-2 text-xs text-emerald-400 hover:text-emerald-300"
                      >
                        Read Less
                      </button>
                    </>
                  )}
                </div>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Show more / less for the whole list */}
      {items.length > 3 && (
        <div className="pt-2">
          <button
            type="button"
            onClick={() => setShowAll((v) => !v)}
            className="text-xs text-emerald-400 hover:text-emerald-300"
          >
            {showAll ? "Show less" : `Show ${items.length - 3} more`}
          </button>
        </div>
      )}
    </Root>
  );
}
